import React, { useEffect, useState } from 'react';
import api from 'api';
import { makeStyles, Typography, CircularProgress } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Store from 'store/store';
import Charts from 'components/Charts';
import { BoxWrapper, Button, Tooltip } from 'components/Base';
import { RiskGroupType } from 'store/modules/Firm/types';
import InfoIcon from '@material-ui/icons/Info';
import PastPerformance from 'components/IPS/PastPerformance';
import ScatterChart from 'components/InvestmentAnalyser/ScatterChart';
import PastPerformanceTable from 'components/IPS/PastPerformanceTable/PastPerformanceTable';
import RiskDescriptionModal from 'components/RiskDescription/RiskDescriptionModal';
import ChargesWrapper from 'components/Policies/Charges/ChargesWrapper';
import { combinePolicyRiskScores } from 'components/Policies/utils';
import BreakDownWrapper from 'components/Charts/Utils/Breakdown/BreakDownWrapper';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolderOutlined';
import Router from 'components/Router/Router';
import { comfortRangeConfig } from 'components/RiskMapping/utils';
import { getComfortRanges } from 'components/Charts/Utils/MorningstarRiskProfiler/utils';
import actions from 'store/actions';

const { useHistory } = Router;

const { connect, selectors } = Store;

const useStyles = makeStyles({
  container: {
    flexWrap: 'wrap'
  },
  box: {
    height: '100%',
    boxShadow: 'none'
  },
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  },
  textRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    marginBottom: '1rem',

    '& strong': {
      marginRight: '2px'
    }
  },
  errorText: {
    fontSize: '0.8125rem',
    margin: 0,
    color: '#ec6a21',
    lineHeight: '1'
  },
  errorBox: {
    display: 'flex',
    padding: '1rem 1rem 0.5rem 1rem',
    alignItems: 'center'
  },
  icon: {
    fill: '#ec6a21',
    width: '1.5rem',
    marginRight: '1rem'
  },
  chartBox: {
    height: '100%',
    padding: '2rem'
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  }
});
interface PolicyAnalysisProps {
  existingPolicyRiskScore: any;
  existingPolicyCharges: any;
  existingPoliciesRiskScore: any;
  recommendedPoliciesRiskScore: any;
  pastPerformance: any;
  existingPoliciesPerformance: any;
  firmRiskGroup: RiskGroupType;
  isPastPerformanceLoading: boolean;
  portfolio: string;
  riskSummaries: any;
  caseInfo: any;
  recommendedRiskAnalysis: any;
  setPastPerformance: Function;
}

const PolicyAnalysis = ({
  existingPolicyRiskScore,
  existingPoliciesRiskScore,
  recommendedPoliciesRiskScore,
  pastPerformance,
  isPastPerformanceLoading,
  firmRiskGroup,
  existingPoliciesPerformance,
  portfolio,
  riskSummaries,
  caseInfo,
  recommendedRiskAnalysis,
  setPastPerformance
}: PolicyAnalysisProps) => {
  const history = useHistory();
  const [error, setError] = useState('');
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);
  const classes = useStyles();
  const isAnyPolicyAdded = existingPoliciesRiskScore.length > 0;
  const [comfortRanges, setComfortRanges] = useState([] as any);

  useEffect(() => {
    const ranges = getComfortRanges(caseInfo.riskProfiler, comfortRangeConfig, [
      recommendedRiskAnalysis['comfort_ranges']
    ]);
    setComfortRanges(ranges);
  }, [caseInfo.riskProfiler, comfortRangeConfig]);

  useEffect(() => {
    if (
      pastPerformance &&
      pastPerformance.failed_codes &&
      pastPerformance.failed_codes.length === 0
    )
      setError('');
    if (
      pastPerformance &&
      pastPerformance.failed_codes &&
      pastPerformance.failed_codes.length > 0
    ) {
      setError(
        `The following funds have been removed from the performance analysis as there is no past performance data available: ${pastPerformance.failed_codes.join(
          ', '
        )}`
      );
    }
    // eslint-disable-next-line
  }, [pastPerformance, pastPerformance.failed_codes]);

  const modifiedData = combinePolicyRiskScores(
    existingPoliciesRiskScore,
    recommendedPoliciesRiskScore
  );

  return (
    <>
      {!isAnyPolicyAdded ? (
        <>
          {' '}
          <div
            style={{
              textAlign: 'center',
              marginBottom: '1rem',
              padding: '2rem'
            }}
          >
            <CreateNewFolderIcon
              htmlColor="#6b7280"
              style={{ width: '36px', height: '36px' }}
            />
            <div style={{ fontWeight: 600 }}>No Existing Policies</div>
            <div style={{ color: '#6b7280' }}>
              Get started by adding a new existing policy.
            </div>
            <div
              style={{
                padding: '1rem 0rem 0rem'
              }}
            >
              <Button
                onClick={() => history.push(`/${portfolio}/existing-policies`)}
              >
                Add Policy
              </Button>
            </div>
          </div>
        </>
      ) : (
        <Grid container spacing={2} className={classes.container}>
          <Grid
            container
            spacing={2}
            className={classes.container}
            style={{
              position: 'relative',
              margin: '0',
              height: '100%',
              marginLeft: '8px',
              marginRight: '8px'
            }}
          >
            {/* Check if riskProfiler is not 'EValue' */}
            {caseInfo.riskProfiler !== 'EValue' && !!comfortRanges ? (
              <Grid item xs={12} md={12} style={{ minHeight: '27rem' }}>
                <BoxWrapper className={classes.box} title="Risk Analysis">
                  <div style={{ padding: '0 5rem' }}>
                    <Charts.MorningstarRiskProfiler.PolicyAnalysis
                      existingOnly={true}
                      ranges={comfortRanges}
                      markers={[
                        {
                          position: 45,
                          type: 'dotted',
                          label: 'Current Position'
                        },
                        { position: 75, type: 'solid', label: 'High Threshold' }
                      ]}
                    />
                  </div>
                </BoxWrapper>
              </Grid>
            ) : (
              <>
                {/* Check if riskSummaries is empty or lacks time_horizon */}
                {riskSummaries.length === 0 ||
                !riskSummaries[0]?.time_horizon ? (
                  <div
                    style={{
                      position: 'absolute',
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.6)',
                      zIndex: '999',
                      width: '100%',
                      color: 'black',
                      fontSize: '1rem',
                      borderRadius: '.5rem',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        alignItems: 'center'
                      }}
                    >
                      Please complete the investment time horizon on the risk
                      analysis page{' '}
                      <div>
                        <Button
                          variant="contained"
                          color="secondary"
                          type="button"
                          onClick={() =>
                            history.push(`/${portfolio}/risk-summary`)
                          }
                        >
                          View Risk Analysis
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Grid item xs={12} md={6} style={{ minHeight: '27rem' }}>
                    <BoxWrapper
                      className={classes.box}
                      title={
                        <div className={classes.flex}>
                          Aggregate risk score
                          <Tooltip text="Excludes any allocation to Wrap cash, where applicable." />
                        </div>
                      }
                      headerChildren={
                        existingPolicyRiskScore?.risk_score ? (
                          <Button
                            variant="contained"
                            color="secondary"
                            type="button"
                            onClick={() => setIsRiskModalOpen(true)}
                          >
                            View Risk
                          </Button>
                        ) : null
                      }
                    >
                      <Charts.ExistingPolicyAnalysis.RiskScore
                        data={existingPolicyRiskScore}
                        riskGroup={firmRiskGroup}
                      />
                    </BoxWrapper>
                  </Grid>
                )}
                {existingPolicyRiskScore?.risk_score && (
                  <RiskDescriptionModal
                    score={existingPolicyRiskScore.risk_score}
                    isOpen={isRiskModalOpen}
                    setIsOpen={setIsRiskModalOpen}
                  />
                )}
                <Grid item xs={12} md={6} style={{ minHeight: '27rem' }}>
                  <BoxWrapper title="Policy risk score" className={classes.box}>
                    <Charts.ExistingPolicyAnalysis.PoliciesRiskScore
                      data={modifiedData}
                      riskGroup={firmRiskGroup}
                      existingOnly={true}
                    />
                  </BoxWrapper>
                </Grid>
              </>
            )}
          </Grid>
          <Grid item xs={12} md={12}>
            <ChargesWrapper existingOnly={true} />
          </Grid>
          <Grid item xs={12}>
            <BoxWrapper
              title="Past performance"
              helperText={
                <Typography variant="subtitle2" className={classes.text}>
                  <div>
                    Performance is based on the current fund allocation and
                    doesn't take into account any previous fund switches.
                  </div>
                  <div className={classes.textRow}>
                    <strong>Performance basis: </strong> Total cumulative return
                  </div>
                  <div className={classes.textRow}>
                    <strong>Pricing Spread: </strong> Bid-Bid
                  </div>
                  <div className={classes.textRow}>
                    <strong>Data Frequency: </strong> Daily
                  </div>
                  <div className={classes.textRow}>
                    <strong>Currency: </strong> Pounds Sterling
                  </div>
                </Typography>
              }
              className={classes.box}
              info={pastPerformance.date_range}
              style={{ paddingBottom: '1rem', minHeight: '25.125rem' }}
            >
              {isPastPerformanceLoading ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <>
                  {error.length !== 0 && (
                    <div className={classes.errorBox}>
                      <InfoIcon className={classes.icon} />
                      <p className={classes.errorText}>{error}</p>
                    </div>
                  )}
                  <PastPerformance
                    data={pastPerformance.data}
                    isAnyPolicyAdded={isAnyPolicyAdded}
                    getPastPerformanceData={api.getPastPerformanceData}
                    earliestDates={pastPerformance.earliest_dates}
                    footprints={pastPerformance.footprints}
                    limits={pastPerformance.limits}
                    setData={(values: any) => {
                      setPastPerformance({
                        ...pastPerformance,
                        ...values
                      });
                    }}
                  />
                  {pastPerformance.tabular_data &&
                    Object.keys(pastPerformance.tabular_data).length > 0 && (
                      <div style={{ margin: '1rem 1rem 0 1rem' }}>
                        <PastPerformanceTable
                          existingOnly={true}
                          data={pastPerformance.tabular_data}
                        />
                      </div>
                    )}
                </>
              )}
            </BoxWrapper>
          </Grid>
          <Grid item xs={12} md={12} style={{ minHeight: '30rem' }}>
            <BoxWrapper title="Scatter Chart" className={classes.box}>
              {isPastPerformanceLoading ? (
                <div className={classes.loaderWrapper}>
                  <CircularProgress size="80px" />
                </div>
              ) : (
                <Grid item xs={12} className={classes.chartBox}>
                  <ScatterChart
                    data={existingPoliciesPerformance}
                    performanceData={pastPerformance.tabular_data}
                  />
                </Grid>
              )}
            </BoxWrapper>
          </Grid>
          <Grid item xs={12}>
            <BreakDownWrapper existingOnly={true} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default connect(
  (state: any) => ({
    existingPolicyRiskScore:
      selectors.caseSelectors.existingPolicyRiskScore(state),
    existingPolicyCharges:
      selectors.caseSelectors.existingPoliciesCharges(state),
    existingPoliciesRiskScore:
      selectors.caseSelectors.existingPoliciesRiskScore(state),
    recommendedPoliciesRiskScore:
      selectors.caseSelectors.recommendedPoliciesRiskScore(state),
    pastPerformance: selectors.caseSelectors.pastPerformance(state),
    existingPoliciesPerformance:
      selectors.caseSelectors.existingPoliciesPerformance(state),
    firmRiskGroup: selectors.firmSelectors.riskGroup(state),
    assetAllocations: selectors.caseSelectors.assetAllocations(state),
    riskSummaries: selectors.caseSelectors.getRiskSummary(state),
    recommendedRiskAnalysis:
      selectors.caseSelectors.recommendedRiskAnalysis(state),
    caseInfo: selectors.caseSelectors.case(state)
  }),
  {
    setPastPerformance: actions.caseActions.setPastPerformance
  }
)(PolicyAnalysis);
